/**
 * Stable sponsor organization (directory) vs per-event participation in `sponsors`.
 */

export function normalizeSponsorOrgName(name) {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Contacts suitable for Firestore org or participation denorm.
 */
export function normalizeContactsForSave(contacts) {
  return (contacts || [])
    .map((c) => ({
      name: (c.name || '').trim() || null,
      email: (c.email || '').trim() || null,
      phone: (c.phone || '').trim() || null,
      note: (c.note || '').trim() || null,
      active: c.active !== false,
      retiredAt: c.retiredAt || null,
    }))
    .filter((c) => c.name || c.email);
}

/**
 * Build org-shaped object fields from form state (before write).
 */
export function buildOrganizationFieldsFromForm({
  name,
  website,
  logo,
  contacts,
  mainContactIndex,
  invoiceContactIndex,
  notes,
}) {
  const trimmedName = (name || '').trim();
  const normalizedContacts = normalizeContactsForSave(contacts);
  return {
    name: trimmedName,
    normalizedName: normalizeSponsorOrgName(trimmedName),
    website: (website || '').trim() || null,
    logo: (logo || '').trim() || null,
    contacts: normalizedContacts,
    mainContactIndex: Math.min(
      Number(mainContactIndex) || 0,
      Math.max(0, normalizedContacts.length - 1)
    ),
    invoiceContactIndex: Math.min(
      Number(invoiceContactIndex) || 0,
      Math.max(0, normalizedContacts.length - 1)
    ),
    notes: (notes || '').trim() || null,
  };
}

/**
 * Denormalized snapshot for participation docs and public reads (from org-shaped data).
 */
export function denormalizedParticipationFromOrg(org) {
  const contacts = normalizeContactsForSave(org.contacts);
  const activeList = contacts.filter((c) => c.active !== false);
  const listForPrimary = activeList.length ? activeList : contacts;
  const mainIdx = Math.min(
    Number(org.mainContactIndex) || 0,
    Math.max(0, listForPrimary.length - 1)
  );
  const primary = listForPrimary[mainIdx] || listForPrimary[0] || {};
  return {
    name: (org.name || '').trim(),
    website: (org.website || '').trim() || null,
    logo: (org.logo || '').trim() || null,
    contactEmail: primary.email || null,
    primaryContactName: primary.name || null,
    contacts,
    mainContactIndex: org.mainContactIndex ?? 0,
    invoiceContactIndex: org.invoiceContactIndex ?? 0,
  };
}

/**
 * Merge participation doc with loaded organization for admin display.
 */
export function mergeParticipationWithOrg(participation, orgById) {
  const oid = participation.organizationId;
  if (!oid || !orgById || !orgById.get) return participation;
  const org = orgById.get(oid);
  if (!org) return participation;
  const denorm = denormalizedParticipationFromOrg(org);
  return {
    ...participation,
    name: denorm.name || participation.name,
    website: denorm.website ?? participation.website,
    logo: denorm.logo ?? participation.logo,
    contactEmail: denorm.contactEmail ?? participation.contactEmail,
    primaryContactName: denorm.primaryContactName ?? participation.primaryContactName,
    contacts: denorm.contacts.length ? denorm.contacts : participation.contacts || [],
    mainContactIndex: org.mainContactIndex ?? participation.mainContactIndex ?? 0,
    invoiceContactIndex: org.invoiceContactIndex ?? participation.invoiceContactIndex ?? 0,
    _directoryNotes: org.notes != null ? org.notes : participation._directoryNotes,
    _directoryUpdatedAt: org.updatedAt ?? participation._directoryUpdatedAt ?? null,
  };
}
